import { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface PDFPreviewProps {
  fileUrl: string;
  onDownload?: () => void;
  onOpenInNewTab?: () => void;
}

export function PDFPreview({ fileUrl, onDownload, onOpenInNewTab }: PDFPreviewProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [scale, setScale] = useState(1);
  const hasCalculatedScale = useRef(false);

  const CONTAINER_WIDTH = 500;
  const CONTAINER_HEIGHT = 600;

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }

  function onPageLoadSuccess(page: { width: number; height: number }) {
    if (!hasCalculatedScale.current) {
      const { width, height } = page;
      const widthScale = (CONTAINER_WIDTH - 40) / width;
      const heightScale = (CONTAINER_HEIGHT - 40) / height;
      setScale(Math.min(widthScale, heightScale));
      hasCalculatedScale.current = true;
    }
  }

  useEffect(() => {
    hasCalculatedScale.current = false;
  }, [pageNumber]);

  function onDocumentLoadError(err: Error) {
    console.error('Error loading PDF:', err);
    setError('Impossible de charger le document PDF');
    setLoading(false);
  }

  const hasActions = onDownload || onOpenInNewTab;

  return (
    <div>
      {loading && (
        <div className="border-2 border-dashed border-neutral-200 bg-neutral-50 p-6 text-center rounded-lg">
          <p className="text-sm text-neutral-500">Chargement du PDF...</p>
        </div>
      )}

      {error && (
        <div className="border border-red-200 bg-red-50 p-4 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading=""
      >
        <div
          className="relative border border-neutral-200 rounded-lg overflow-hidden bg-neutral-50 flex items-center justify-center"
          style={{ width: `${CONTAINER_WIDTH}px`, height: `${CONTAINER_HEIGHT}px` }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            onLoadSuccess={onPageLoadSuccess}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />

          {hasActions && (
            <div
              className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-3 transition-opacity duration-200 ${
                isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-dark-600 text-sm font-medium rounded-lg hover:bg-neutral-100 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Telecharger
                </button>
              )}
              {onOpenInNewTab && (
                <button
                  onClick={onOpenInNewTab}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir
                </button>
              )}
            </div>
          )}
        </div>
      </Document>

      {numPages && numPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
            disabled={pageNumber <= 1}
            className="p-1.5 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-neutral-600">
            Page {pageNumber} sur {numPages}
          </span>
          <button
            onClick={() => setPageNumber((p) => Math.min(p + 1, numPages || 1))}
            disabled={pageNumber >= numPages}
            className="p-1.5 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
